import { useCallback, useEffect, useRef } from 'react';
import { apiFetchBase } from 'src/core/fetch';
import captureException from 'src/common/utils/captureException';
import useNotificationSubscription from './useNotificationSubscription';
import useInterval from './useInterval';
import useDebouncedCallback from './useDebouncedCallback';

const FALLBACK_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes
const DEBOUNCE_MS = 2000;

type UnreadResponse = { count: number };

/**
 * Keeps the unread thread count in sync with the server by:
 * 1. Reacting to PubSub `notification` and `seen` events (debounced fetch).
 * 2. Re-fetching when the tab regains visibility.
 * 3. Running a slow background poll as a safety net.
 *
 * Only active when `enabled` is true (i.e. user is authenticated).
 */
const useUnreadCount = (
  enabled: boolean,
  onCount: (count: number) => void,
): void => {
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiFetchBase('trips/unread/');
      if (response.ok) {
        const data: UnreadResponse = await response.json();
        onCountRef.current(data.count);
      }
    } catch (err) {
      captureException(err, { extra: { fn: 'fetchUnreadCount' } });
    }
  }, []);

  const debouncedFetch = useDebouncedCallback(fetchUnreadCount, DEBOUNCE_MS);

  const fetchUnreadCountDebounced = useCallback(() => {
    if (!enabled) {
      return;
    }
    debouncedFetch();
  }, [enabled, debouncedFetch]);

  useNotificationSubscription('notification', fetchUnreadCountDebounced);

  useNotificationSubscription('seen', fetchUnreadCountDebounced);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCountDebounced();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, fetchUnreadCountDebounced]);

  useInterval(
    useCallback(() => {
      if (document.visibilityState === 'visible') {
        fetchUnreadCount();
      }
    }, [fetchUnreadCount]),
    FALLBACK_POLL_INTERVAL_MS,
    enabled,
  );
};

export default useUnreadCount;
